import { createWebHistory, createRouter } from "vue-router";
import axios from "axios";
import routes from "./routes";
import appConfig from "../../app.config";

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: "history",
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

// API based Authentication
// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);

  if (!authRequired) return next();

  const token = localStorage.getItem("jwt");
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user ? user.id : null;

  if (!token || !userId) {
    return next({ name: "login", query: { redirectFrom: routeTo.fullPath } });
  }

  // To avoid redundant profile calls on every route, we can skip if userdata exists
  // but if we want fresh data, we keep it. For now, let's optimize it.
  const userdata = localStorage.getItem("userdata");
  if (userdata && routeFrom.name) {
     return next();
  }

  try {
    const response = await axios.post(
        `${process.env.VUE_APP_URL}/auth/profile`,
      {},
      {
        headers: {
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const freshUser = response.data.data;
    localStorage.setItem("userdata", JSON.stringify(freshUser));
    localStorage.setItem("user", JSON.stringify(freshUser));
    
    next();
  } catch (error) {
    console.error("Auth profile check failed:", error);
    localStorage.removeItem("jwt");
    localStorage.removeItem("user");
    localStorage.removeItem("userdata");
    next({ name: "login", query: { redirectFrom: routeTo.fullPath } });
  }
});

// Remove the second redundant beforeEach hook

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + " | " + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
